import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FilePlus2, ListRestart, Loader2, Smartphone, Mail, ShieldCheck, FileText, UserRound, ScanFace, Home } from "lucide-react";
import { toast } from "sonner";
import { AnswerPill, AuthImg, LabelPill, fmtTs, nice } from "./sumsubShared";
import { SumsubDocs } from "./SumsubDocs";
import { runAction } from "./SumsubActions";

const ICON = { APPLICANT_DATA: UserRound, IDENTITY: FileText, SELFIE: ScanFace, PROOF_OF_RESIDENCE: Home, PHONE_VERIFICATION: Smartphone, EMAIL_VERIFICATION: Mail };
const CHECK_OF = { PHONE_VERIFICATION: "PHONE_CONFIRMATION", EMAIL_VERIFICATION: "EMAIL_CONFIRMATION", PROOF_OF_RESIDENCE: "POA" };

function ResetStep({ applicantId, step, canAct, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await runAction(applicantId, "reset_step", { step: step.key });
      toast.success(`${step.label} reset — the applicant re-does this step on the same link`);
      setOpen(false);
      onDone(r);
    } catch (e) { toast.error(e.response?.data?.detail || e.message, { duration: 8000 }); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={!canAct}>
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-amber-800 hover:bg-amber-50" disabled={!canAct} title={canAct ? "Clear only this step" : "Underwriting permission needed"} data-testid={`sumsub-step-reset-${step.key}`}>
          <ListRestart className="w-3.5 h-3.5 mr-1" />Reset
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-2 p-3" data-testid={`sumsub-step-reset-pop-${step.key}`}>
        <div className="text-sm font-semibold">Reset “{step.label}”?</div>
        <p className="text-[11px] text-slate-600">Only this step is cleared; the applicant re-does it on the same link. Everything else they completed stays.</p>
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button size="sm" className="h-7 text-xs bg-amber-600 text-white hover:bg-amber-700" onClick={go} disabled={busy} data-testid={`sumsub-step-reset-confirm-${step.key}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ListRestart className="w-3.5 h-3.5 mr-1" />}Reset step
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StepCard({ d, s, canAct, onDone, onAddDoc }) {
  const a = d.applicant;
  const Icon = ICON[s.key] || ShieldCheck;
  const check = d.checks?.[CHECK_OF[s.key]];
  const tone = s.answer === "GREEN" ? "border-emerald-200" : s.answer === "RED" ? "border-red-200" : s.present ? "border-slate-200" : "border-dashed border-slate-200";
  const cf = s.key === "APPLICANT_DATA" ? (a.custom_fields || []) : [];
  const canAddDoc = (s.key === "IDENTITY" || s.key === "PROOF_OF_RESIDENCE") && onAddDoc;
  return (
    <div className={`rounded-lg border bg-white p-3 shadow-sm ${tone}`} data-testid={`sumsub-step-${s.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`flex h-7 w-7 items-center justify-center rounded-full ${s.answer === "GREEN" ? "bg-emerald-100 text-emerald-700" : s.answer === "RED" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}><Icon className="w-3.5 h-3.5" /></span>
          <div>
            <div className="text-sm font-medium text-slate-900">{s.label}</div>
            <div className="text-[11px] text-slate-500">{[s.doc_type && nice(s.doc_type), s.country, !s.present && (s.required ? "not started" : null), s.doc_types?.length && !s.doc_type ? `accepts ${s.doc_types.map(nice).join(", ")}` : null].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <AnswerPill answer={s.answer} testId={`sumsub-step-answer-${s.key}`} />
          {canAddDoc && (
            <Button size="sm" variant="ghost" className={`h-6 px-1.5 text-[11px] ${s.answer === "RED" ? "text-sky-800 hover:bg-sky-50" : "text-slate-600"}`} disabled={!canAct} onClick={() => onAddDoc(s.key)}
              title={canAct ? "Add a document to this step from Monday or this computer, then re-check" : "Underwriting permission needed"} data-testid={`sumsub-step-adddoc-${s.key}`}>
              <FilePlus2 className="w-3.5 h-3.5 mr-1" />Add document
            </Button>
          )}
          {s.present && <ResetStep applicantId={a.id} step={s} canAct={canAct} onDone={onDone} />}
        </div>
      </div>
      {s.reject_labels?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{s.reject_labels.map((l) => <LabelPill key={l} tone="red">{l}</LabelPill>)}{s.reject_type && <LabelPill tone="amber">{s.reject_type}</LabelPill>}</div>}
      {s.moderation_comment && <p className="mt-1.5 text-xs text-slate-700">“{s.moderation_comment}”</p>}
      {check && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-600">
          <CheckCircle2 className={`w-3.5 h-3.5 ${check.answer === "GREEN" ? "text-emerald-600" : "text-slate-400"}`} />
          {check.checkType === "POA" ? "Address check" : "Confirmed via OTP"} · {fmtTs(check.createdAt)}
        </div>
      )}
      {s.image_ids?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {s.image_ids.map((id) => (
            <div key={id} className="relative h-20 w-28 overflow-hidden rounded border bg-slate-100">
              <AuthImg applicantId={a.id} imageId={id} inspectionId={a.inspection_id} preview className="h-full w-full object-cover" />
              <div className="absolute bottom-0.5 left-0.5"><AnswerPill answer={s.image_results?.[id]} /></div>
            </div>
          ))}
        </div>
      )}
      {cf.length > 0 && (
        <div className="mt-2 grid grid-cols-1 gap-x-3 gap-y-0.5 text-[11px] sm:grid-cols-2">
          {cf.map((m) => <div key={m.key} className="truncate"><span className="text-slate-500">{m.key}:</span> <span className="text-slate-900">{m.value || "—"}</span></div>)}
        </div>
      )}
    </div>
  );
}

// "Checks" tab: every verification step as a card with its result, images and an inline Reset; documents below.
export function SumsubChecks({ d, canAct, onDone, onAddDoc }) {
  const a = d.applicant;
  return (
    <div className="space-y-3" data-testid="sumsub-steps">
      <div className="grid gap-2 md:grid-cols-2">
        {d.steps.map((s) => <StepCard key={s.key} d={d} s={s} canAct={canAct} onDone={onDone} onAddDoc={onAddDoc} />)}
      </div>
      <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="sumsub-docs-card">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Documents · {d.documents.length}</h3>
        <SumsubDocs applicantId={a.id} inspectionId={a.inspection_id} documents={d.documents} />
      </section>
    </div>
  );
}
